import { useQuery } from '@tanstack/react-query';
import { CampaignMedium } from '@prisma/client';
import { CampaignSegment } from '../../types/marketing';
import useGetMarketingData from './useGetMarketingData';
import { getCombinedSegmentCustomers } from '../../services/marketing';

interface Params {
  includeSegment: CampaignSegment | null;
  excludeSegment: CampaignSegment | null;
  medium: CampaignMedium | null;
  enabled?: boolean;
}

function useGetCombinedSegmentCustomers({
  includeSegment,
  excludeSegment,
  medium,
  enabled = true,
}: Params) {
  const { data: marketingData, isFetching: isFetchingMarketing } =
    useGetMarketingData();

  const {
    data,
    isFetching,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
    isPlaceholderData,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'campaign-combined-segment-customers',
      marketingData?.id,
      includeSegment?.id,
      includeSegment?.offboardingId,
      excludeSegment?.id,
      medium,
    ],
    queryFn: async () => {
      const result = await getCombinedSegmentCustomers(marketingData?.id!, {
        includeSegment,
        excludeSegment,
        medium,
      });
      return result.totalCount || 0;
    },
    enabled: !!marketingData?.id && !isFetchingMarketing && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    error,
    isSuccess,
    isPlaceholderData,
  };
}

export default useGetCombinedSegmentCustomers;
